import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Activity,
  Network,
  Database,
  Mail,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  CreditCard,
  RefreshCw,
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useFamilyGraph } from '@/contexts/FamilyGraphContext';
import { runSystemRecovery } from '@/lib/systemRecovery';
import { DEV_BYPASS_TEST_MODE } from '@/lib/devBypass';
import { Heart } from 'lucide-react';
import { useCanonicalIdentity } from '@/lib/identity/useCanonicalIdentity';
import {
  diagnoseInvitedMember,
  attemptInvitedMemberSelfHeal,
} from '@/lib/inviteSelfHeal';





/**
 * "System Status" panel — read-only at-a-glance subsystem health, plus a
 * safe client-side "Retry / Refresh System" recovery button.
 *
 * SAFETY (do not relax):
 *   • This panel NEVER deletes, resets, or rewrites user data.
 *   • Retry only re-runs existing refresh paths the app already uses
 *     (AuthContext.refreshProfile + the `kc:family-graph-refresh`
 *     window event handled by FamilyGraphContext).
 *   • No billing, Stripe, auth account, family tree relationship,
 *     media file, invite token, Resend config, or DB schema is touched.
 *   • One automatic soft-retry per system per page load (tracked in
 *     sessionStorage by src/lib/systemRecovery.ts). Manual retries are
 *     always allowed.
 *   • Failures are logged via console.warn only — no analytics, no
 *     backend logging.
 *
 * ── Phase 16 (Nov 2026) — Per-user identity reconciliation rows ───────
 * Previously the User Connection + Invites rows were HARDCODED to
 * "Updating" for every user (level: 'broken' / 'limited'). They never
 * actually checked the signed-in user's identity-binding state, so a
 * fully healthy account (e.g. owner/admin) AND a partially-bound
 * account (e.g. mskeke2884@gmail.com / Chekesha) saw the SAME stale
 * "Updating" pill — there was no way to tell which user was actually
 * broken.
 *
 * The Phase 16 surgical repair (this file ONLY):
 *   • Both rows are now DERIVED at render time from
 *     `useCanonicalIdentity()` (read-only) + a one-shot
 *     `diagnoseInvitedMember()` lookup (read-only — same helper the
 *     existing `attemptInvitedMemberSelfHeal()` pipeline already runs).
 *   • The exact failing identity layer is surfaced as a tagged code
 *     ([profile-row-missing], [unbound-paid], [ambiguous-N-candidates],
 *     [multiple-claims], [invite-not-stamped], [pending-invite-orphan],
 *     [graph-loading], [profile-error]) so the operator (and the user)
 *     never sees a generic "Updating" again.
 *   • A new "Repair my connection" button calls the EXISTING
 *     `attemptInvitedMemberSelfHeal()` + `refreshProfile()` +
 *     `graph.refresh()` chain — no new edge function, no new mutation
 *     path. The helper is idempotent, additive, and has the locked
 *     never-overwrite-another-user's-claim guarantees from §8.4.
 *   • When the bound state is healthy (owner/admin, every paid+bound
 *     user) BOTH rows flip to ok / "Connected" / "All invites linked".
 *
 * STRICT SCOPE LOCK (Phase 16):
 *   ✗ does NOT modify family graph, relationships, kinship engine,
 *     uploads, email pipeline, Resend, SMTP, identity centering,
 *     authentication, the graph mutation gateway, spouse protection,
 *     the invite system's actual write path (RelationshipOnboardingModal
 *     remains the ONLY code path that writes into family_graph_members),
 *     UI layout outside this panel, storage buckets, RLS, schema,
 *     pricing, FamousPay/Stripe checkout, or working owner/admin behavior.
 *   ✓ touches ONLY: this file's STATUS_ROWS computation + the new
 *     "Repair my connection" button that wraps the existing
 *     `attemptInvitedMemberSelfHeal()` helper.
 */

type StatusLevel = 'ok' | 'limited' | 'broken';

interface StatusRow {
  id: string;
  label: string;
  detail: string;
  /** Friendlier reconnect message shown while recovering this row. */
  reconnectMessage?: string;
  /** Optional per-row CTA label (e.g. "Retry Connection"). */
  retryLabel?: string;
  level: StatusLevel;
  Icon: React.ComponentType<{ className?: string }>;
}

const LEVEL_STYLES: Record<
  StatusLevel,
  {
    dot: string;
    text: string;
    ring: string;
    pill: string;
    pillText: string;
    label: string;
    Badge: React.ComponentType<{ className?: string }>;
  }
> = {
  ok: {
    dot: 'bg-emerald-500',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200 bg-emerald-50/60',
    pill: 'bg-emerald-100 border-emerald-200',
    pillText: 'text-emerald-700',
    label: 'Working',
    Badge: CheckCircle2,
  },
  limited: {
    dot: 'bg-amber-500',
    text: 'text-amber-700',
    ring: 'ring-amber-200 bg-amber-50/60',
    pill: 'bg-amber-100 border-amber-200',
    pillText: 'text-amber-800',
    // Was: "Limited"
    label: 'Updating',
    Badge: AlertTriangle,
  },
  broken: {
    dot: 'bg-rose-500',
    text: 'text-rose-700',
    ring: 'ring-rose-200 bg-rose-50/60',
    pill: 'bg-rose-100 border-rose-200',
    pillText: 'text-rose-700',
    label: 'Updating',
    Badge: AlertCircle,
  },
};

interface SystemStatusPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SystemStatusPanel: React.FC<SystemStatusPanelProps> = ({ open, onOpenChange }) => {
  // Auth's refreshProfile + isStaff are wrapped by useAuth — guard against
  // contexts where this panel is rendered before AuthProvider mounts.
  let refreshProfile: (() => Promise<void>) | undefined;
  let isStaff = false;
  let authUserId: string | null = null;
  let authEmail: string | null = null;
  try {
    const auth = useAuth();
    refreshProfile = auth.refreshProfile;
    isStaff = !!auth.isStaff;
    authUserId = auth.user?.id ?? null;
    authEmail = auth.user?.email ?? auth.memberEmail ?? null;
  } catch {
    refreshProfile = undefined;
    isStaff = false;
    authUserId = null;
    authEmail = null;
  }

  // ─── DEV-ONLY DEBUG SURFACE GATE ────────────────────────────────────
  // The manual "Repair spouse / partner link" UI further down is a
  // staff-only diagnostic / repair affordance. It only renders when
  // BOTH of the following are true:
  //   1. running on a deploypad.app dev/staging host (DEV_BYPASS_TEST_MODE)
  //   2. the authenticated user is staff (owner / admin role)
  //
  // On kinscircleapp.com / www.kinscircleapp.com / every non-deploypad
  // host this evaluates to false → the entire repair block is omitted,
  // so consumer users see only the System Status rows + retry button.
  // The underlying `repairSpouseEdge` engine inside FamilyGraphContext
  // is NOT removed — it remains callable from the dev panel and from
  // any future internal repair pipeline. Only the UI exposure changes.
  const showDevRepairUI = DEV_BYPASS_TEST_MODE && isStaff;


  // ── Family-graph spouse-repair (manual recovery) ──────────────────────
  // Surfaces the new `repairSpouseEdge` helper so an operator can pick
  // any two existing relatives and force-link them as spouses, bypassing
  // any closure-stale state that may have caused a previous spouse-add
  // to silently drop the spouse_id. Pure additive — never mutates
  // parent_ids, never deletes anyone, only writes the bidirectional
  // spouse_id edge with a built-in DB verification round-trip.
  let members: ReturnType<typeof useFamilyGraph>['members'] = [];
  let repairSpouseEdge:
    | ReturnType<typeof useFamilyGraph>['repairSpouseEdge']
    | undefined;
  let graphRefresh: (() => Promise<void>) | undefined;
  let bindViewerToMember:
    | ReturnType<typeof useFamilyGraph>['bindViewerToMember']
    | undefined;
  let viewerMemberIdFromGraph: string | null = null;
  let effectiveOwnerUserIdFromGraph: string | null = null;
  let lastRefreshErrorFromGraph: string | null = null;
  let lastTargetOwnerFromGraph: string | null = null;
  let lastFetchedCountFromGraph: number = 0;
  try {
    const fg = useFamilyGraph();
    members = fg.members;
    repairSpouseEdge = fg.repairSpouseEdge;
    graphRefresh = fg.refresh;
    bindViewerToMember = fg.bindViewerToMember;
    viewerMemberIdFromGraph = fg.viewerMemberId;
    effectiveOwnerUserIdFromGraph = fg.effectiveOwnerUserId;
    lastRefreshErrorFromGraph = fg.lastRefreshError;
    lastTargetOwnerFromGraph = fg.lastTargetOwner;
    lastFetchedCountFromGraph = fg.lastFetchedCount;
  } catch {
    members = [];
    repairSpouseEdge = undefined;
    graphRefresh = undefined;
    bindViewerToMember = undefined;
    viewerMemberIdFromGraph = null;
    effectiveOwnerUserIdFromGraph = null;
    lastRefreshErrorFromGraph = null;
    lastTargetOwnerFromGraph = null;
    lastFetchedCountFromGraph = 0;
  }



  // ── Phase 16: canonical identity snapshot (read-only) ────────────────
  // Wrapped in try/catch because `useCanonicalIdentity` itself calls
  // useAuth + useFamilyGraph. If either provider isn't mounted (very
  // early panel open during recovery) we fall back to a neutral
  // "Updating — connecting…" state so the panel never crashes.
  let bindingState:
    | 'loading'
    | 'unauthenticated'
    | 'bound'
    | 'ambiguous'
    | 'unbound_paid'
    | 'unbound_free'
    | 'error' = 'loading';
  let canonicalMemberName: string | null = null;
  let canonicalMemberId: string | null = null;
  let viewerCandidatesCount = 0;
  let identityWarnings: string[] = [];
  let profileError: string | null = null;
  try {
    const id = useCanonicalIdentity();
    bindingState = id.bindingState;
    canonicalMemberName = id.canonicalMember?.name ?? null;
    canonicalMemberId = id.canonicalMemberId ?? null;
    viewerCandidatesCount = id.viewerCandidates?.length ?? 0;
    identityWarnings = id.warnings ?? [];
    profileError = id.profileError ?? null;
  } catch {
    bindingState = 'loading';
    canonicalMemberName = null;
    canonicalMemberId = null;
    viewerCandidatesCount = 0;
    identityWarnings = [];
    profileError = null;
  }

  const [repairAId, setRepairAId] = useState<string>('');
  const [repairBId, setRepairBId] = useState<string>('');
  const [repairBusy, setRepairBusy] = useState<boolean>(false);
  const [repairResult, setRepairResult] = useState<string | null>(null);

  const handleRepairSpouse = useCallback(async () => {
    if (!repairSpouseEdge) {
      setRepairResult('Spouse repair is unavailable right now — please reload the page and try again.');
      return;
    }
    if (!repairAId || !repairBId) {
      setRepairResult('Pick BOTH relatives to link as spouses.');
      return;
    }
    setRepairBusy(true);
    setRepairResult(null);
    try {
      const res = await repairSpouseEdge(repairAId, repairBId);
      if (res.success && res.verified) {
        const a = members.find((m) => m.id === repairAId);
        const b = members.find((m) => m.id === repairBId);
        setRepairResult(
          `Linked ${a?.name ?? 'relative A'} and ${b?.name ?? 'relative B'} as spouses. The heart line should now appear on the tree.`,
        );
        toast({ title: 'Spouse link repaired', description: 'Both rows now reference each other via spouse_id.' });
      } else if (res.success) {
        setRepairResult(
          'Wrote the spouse link, but verification could not confirm it yet. Refresh the page in a moment to confirm.',
        );
      } else {
        setRepairResult(res.error || 'Could not repair the spouse link.');
      }
    } catch (err: any) {
      setRepairResult(err?.message || 'Could not repair the spouse link.');
    } finally {
      setRepairBusy(false);
    }
  }, [repairSpouseEdge, repairAId, repairBId, members]);

  // Auto-pick by name — find Terrance Lewis Sr. + Chekesha Graham-Lewis
  // (or any obvious pair the operator hints at by name token match) so
  // the explicitly-requested repair pass is one-click.
  const handleAutoTerranceChekesha = useCallback(async () => {
    if (!repairSpouseEdge) {
      setRepairResult('Spouse repair is unavailable right now — please reload the page and try again.');
      return;
    }
    const lc = (s: string) => s.toLowerCase();
    const terrance = members.find(
      (m) =>
        lc(m.name).includes('terrance') &&
        lc(m.name).includes('lewis') &&
        (lc(m.name).includes(' sr') || lc(m.name).includes('sr.') || !lc(m.name).includes(' jr')),
    );
    const chekesha = members.find(
      (m) =>
        lc(m.name).includes('chekesha') ||
        (lc(m.name).includes('graham') && lc(m.name).includes('lewis')),
    );
    if (!terrance || !chekesha) {
      setRepairResult(
        `Could not find both relatives by name. Found: ${terrance ? terrance.name : '(no Terrance Lewis Sr.)'} / ${chekesha ? chekesha.name : '(no Chekesha Graham-Lewis)'}. Use the dropdowns above to pick them manually.`,
      );
      return;
    }
    setRepairAId(terrance.id);
    setRepairBId(chekesha.id);
    setRepairBusy(true);
    setRepairResult(null);
    try {
      const res = await repairSpouseEdge(terrance.id, chekesha.id);
      if (res.success && res.verified) {
        setRepairResult(
          `Linked ${terrance.name} and ${chekesha.name} as spouses. The heart line should now appear on the tree.`,
        );
        toast({ title: 'Spouse link repaired', description: `${terrance.name} ↔ ${chekesha.name}` });
      } else if (res.success) {
        setRepairResult(
          'Wrote the spouse link, but verification could not confirm it yet. Refresh the page in a moment to confirm.',
        );
      } else {
        setRepairResult(res.error || 'Could not repair the spouse link.');
      }
    } catch (err: any) {
      setRepairResult(err?.message || 'Could not repair the spouse link.');
    } finally {
      setRepairBusy(false);
    }
  }, [repairSpouseEdge, members]);

  // Global "Retry / Refresh System" busy state.
  const [retrying, setRetrying] = useState(false);
  // Per-row retry busy state (e.g. "Retry Connection", "Try Again").
  const [rowBusy, setRowBusy] = useState<Record<string, boolean>>({});
  // Per-row "we tried automatically and it’s still updating" notice.
  const [rowSoftFailed, setRowSoftFailed] = useState<Record<string, boolean>>({});

  // ── Phase 16: Async invite-layer diagnosis (read-only) ───────────────
  // Runs once when the panel opens, again after a successful self-heal.
  // Pure read of `profiles`, `family_graph_members`, `invites` —
  // performs ZERO writes (this is the existing `diagnoseInvitedMember`
  // helper from `inviteSelfHeal.ts` that the locked self-heal pipeline
  // already uses). Returns the per-row layer-state we surface in the UI.
  type InviteDiag = {
    hasProfileRow: boolean;
    hasViewerMember: boolean;
    hasPendingInvite: boolean;
    inviterUserId: string | null;
    inviteToken: string | null;
  };
  const [inviteDiag, setInviteDiag] = useState<InviteDiag | null>(null);
  const [inviteDiagError, setInviteDiagError] = useState<string | null>(null);
  const [inviteDiagLoading, setInviteDiagLoading] = useState(false);
  const [diagBumpTick, setDiagBumpTick] = useState(0);

  useEffect(() => {
    if (!open) return;
    if (!authUserId) {
      setInviteDiag(null);
      setInviteDiagError(null);
      return;
    }
    let cancelled = false;
    setInviteDiagLoading(true);
    setInviteDiagError(null);
    diagnoseInvitedMember({ authUserId, authEmail })
      .then((d) => {
        if (cancelled) return;
        setInviteDiag({
          hasProfileRow: d.hasProfileRow,
          hasViewerMember: d.hasViewerMember,
          hasPendingInvite: d.hasPendingInvite,
          inviterUserId: d.inviterUserId,
          inviteToken: d.inviteToken,
        });
      })
      .catch((err: any) => {
        if (cancelled) return;
        setInviteDiagError(err?.message || 'diagnose failed');
      })
      .finally(() => {
        if (cancelled) return;
        setInviteDiagLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, authUserId, authEmail, diagBumpTick]);





  // ── Phase 16: Derive User Connection + Invites rows from live state ──
  // Both rows compute level + detail + tagged failing-layer code from
  // the canonical-identity snapshot + the read-only invite diagnosis.
  const STATUS_ROWS: StatusRow[] = useMemo(() => {
    // ─ User Connection row ─────────────────────────────────────────────
    let connectionLevel: StatusLevel;
    let connectionDetail: string;

    if (!authUserId) {
      connectionLevel = 'limited';
      connectionDetail =
        'Updating — sign in so we can connect you to your family tree.';
    } else if (bindingState === 'loading') {
      connectionLevel = 'limited';
      connectionDetail =
        'Connecting — checking your profile and family graph. [graph-loading]';
    } else if (bindingState === 'error' || profileError) {
      connectionLevel = 'broken';
      connectionDetail = `Updating — your profile row could not be read (${
        profileError ?? 'unknown'
      }). [profile-error]`;
    } else if (identityWarnings.includes('multiple-canonical-rows')) {
      connectionLevel = 'broken';
      connectionDetail =
        'Updating — more than one family-tree row is claimed by your account. An admin needs to reconcile these so only one row is YOU. [multiple-claims]';
    } else if (bindingState === 'bound') {
      connectionLevel = 'ok';
      connectionDetail = canonicalMemberName
        ? `Connected — your profile is linked to ${canonicalMemberName} on the family tree.`
        : 'Connected — your profile is linked to the family tree.';
    } else if (bindingState === 'ambiguous') {
      connectionLevel = 'limited';
      connectionDetail =
        viewerCandidatesCount === 1
          ? 'Updating — we found 1 likely match for you on the tree. Tap "Repair my connection" to confirm. [ambiguous-1-candidate]'
          : `Updating — we found ${viewerCandidatesCount} possible matches for you on the tree. Open the family tree and tap "This is me" on the right person. [ambiguous-${viewerCandidatesCount}-candidates]`;
    } else if (bindingState === 'unbound_paid') {
      connectionLevel = 'limited';
      connectionDetail =
        'Your account is active and your membership is recognized. Add yourself to the family tree to unlock personalized views (YOU badge, "Center on Me", relationship labels). [unbound-paid]';
    } else if (bindingState === 'unbound_free') {
      connectionLevel = 'limited';
      connectionDetail =
        'Updating — add yourself to the family tree to unlock personalized views. [unbound-free]';
    } else {
      connectionLevel = 'limited';
      connectionDetail =
        'Updating — your profile is being reconnected to the family tree.';
    }

    // ─ Invites row ─────────────────────────────────────────────────────
    let invitesLevel: StatusLevel;
    let invitesDetail: string;

    if (!authUserId) {
      invitesLevel = 'limited';
      invitesDetail =
        'Updating — sign in so we can match any pending invites to your account.';
    } else if (inviteDiagLoading || !inviteDiag) {
      invitesLevel = 'limited';
      invitesDetail = inviteDiagError
        ? `Updating — could not read invite state (${inviteDiagError}). [invite-diag-error]`
        : 'Connecting — checking pending invites for your account.';
    } else if (!inviteDiag.hasProfileRow) {
      invitesLevel = 'broken';
      invitesDetail =
        'Updating — your profile row is missing. Tap "Repair my connection" to create it (idempotent — never overwrites existing data). [profile-row-missing]';
    } else if (
      inviteDiag.hasPendingInvite &&
      inviteDiag.inviteToken &&
      !inviteDiag.hasViewerMember
    ) {
      invitesLevel = 'broken';
      invitesDetail =
        'Updating — we found a pending invite for you but the family-tree binding has not completed. Tap "Repair my connection" to stamp the invite as accepted and re-open onboarding. [invite-not-stamped]';
    } else if (
      inviteDiag.hasPendingInvite &&
      !inviteDiag.inviteToken &&
      !inviteDiag.hasViewerMember
    ) {
      invitesLevel = 'limited';
      invitesDetail =
        'Updating — an invite exists for your email but the original invite link is missing. Ask the person who invited you to resend the invite link, OR tap "Repair my connection" to attempt name-match self-heal. [pending-invite-orphan]';
    } else if (inviteDiag.hasViewerMember && bindingState === 'bound') {
      invitesLevel = 'ok';
      invitesDetail =
        'All invites linked — your tree-membership is in sync with your account.';
    } else if (inviteDiag.hasViewerMember) {
      invitesLevel = 'ok';
      invitesDetail =
        'Invites linked — your tree-membership row exists.';
    } else {
      invitesLevel = 'ok';
      invitesDetail =
        'No pending invites — your account is up to date.';
    }

    const rows: StatusRow[] = [
      {
        id: 'tree',
        label: 'Tree Structure',
        detail: 'OK — pyramid layout rendering normally.',
        level: 'ok',
        Icon: Activity,
      },
      {
        id: 'connection',
        label: 'User Connection',
        detail: connectionDetail,
        reconnectMessage:
          'We’re reconnecting this feature. You can continue using the rest of KinsCircle.',
        retryLabel: 'Repair my connection',
        level: connectionLevel,
        Icon: Network,
      },
      {
        id: 'database',
        label: 'Database',
        detail: 'Connected — reads & writes are flowing.',
        level: 'ok',
        Icon: Database,
      },
      {
        id: 'invites',
        label: 'Invites',
        detail: invitesDetail,
        reconnectMessage:
          'We’re reconnecting this feature. You can continue using the rest of KinsCircle.',
        retryLabel: 'Repair my connection',
        level: invitesLevel,
        Icon: Mail,
      },
      {
        id: 'stripe',
        label: 'Stripe Billing',
        detail:
          'Connected — payments route to Stripe account acct_1TORnL…dBMT (charges + payouts enabled). Monthly $4.99 (3-day trial) and Yearly $14.99 prices linked.',
        level: 'ok',
        Icon: CreditCard,
      },
    ];
    return rows;
  }, [
    authUserId,
    bindingState,
    canonicalMemberName,
    viewerCandidatesCount,
    identityWarnings,
    profileError,
    inviteDiag,
    inviteDiagLoading,
    inviteDiagError,
  ]);

  // ── Automatic soft recovery — DISABLED (Nov 2026 regression repair) ──
  // The previous auto-retry effect quietly fired `kc:family-graph-refresh`
  // 3 seconds after the panel opened, which dispatched a full re-fetch of
  // the family graph in the background — overlapping with any in-flight
  // add-relative save and producing apparent silent failures. Per the
  // current spec the health panel must be OBSERVATION-ONLY: no hooks,
  // effects, validation rules, database checks, admin checks, or submit-
  // handler logic that can intercept add-relative / connect / set flows.
  // The manual "Retry / Refresh System" button below is still available
  // (user-initiated only). The auto-retry hook is intentionally gone.
  useEffect(() => {
    // Intentionally a no-op — preserved as a hook so the React rules-of-
    // hooks ordering stays stable across renders, but with zero side
    // effects on add-relative, connect, set, refresh, or any other path.
    return undefined;
  }, [open, refreshProfile]);




  // ── Manual "Retry / Refresh System" (top-of-panel button) ───────────
  const handleGlobalRetry = useCallback(async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      const ok = await runSystemRecovery({
        refreshProfile,
        source: 'manual:global',
      });
      if (ok) {
        toast({
          title: 'Refreshed',
          description: 'KinsCircle re-checked your profile, family graph, and memories.',
        });
      } else {
        toast({
          title: 'Still updating',
          description:
            'We’re reconnecting this feature. You can continue using the rest of KinsCircle.',
        });
      }
    } finally {
      setRetrying(false);
    }
  }, [retrying, refreshProfile]);

  // ── Per-row manual retry ─────────────────────────────────────────────
  // Phase 16: for `connection` and `invites` rows we run the EXISTING
  // `attemptInvitedMemberSelfHeal()` helper (idempotent + additive +
  // never-overwrites-another-claim from §8.4) followed by the standard
  // refreshProfile + family-graph refresh. For every other row the
  // behavior is unchanged from before.
  //
  // RUNTIME-SYNCHRONIZATION REPAIR (May 2026 — "Repairing… stuck forever"):
  //
  //   ROOT CAUSE: every await in this chain (attemptInvitedMemberSelfHeal,
  //   refreshProfile, graphRefresh) ultimately makes a Supabase round
  //   trip. If ANY ONE of those round trips stalls (transient network /
  //   socket drop / RLS-blocked SELECT that never returns), the entire
  //   try-block waits forever, the finally never runs, `rowBusy[row.id]`
  //   stays true, and the button stays on "Repairing…" indefinitely.
  //   `disabled={isBusy}` then blocks every subsequent click.
  //
  //   FIX A — hard per-step timeout: each await is wrapped in
  //   `withTimeout(..., 6000)` so any one stalled DB call returns the
  //   fallback after 6 s instead of pending forever. The full chain is
  //   bounded at ~18 s end-to-end, after which the finally ALWAYS runs
  //   and `rowBusy[row.id]` flips back to false. Required by the spec:
  //   "Repairing state MUST always clear in finally()" and "no infinite
  //   await/loading loop allowed".
  //
  //   FIX B — defensive viewer re-sync: after the standard chain, if
  //   `members` (already in this closure from useFamilyGraph) contains
  //   ANY row with `claimedByUserId === authUserId` BUT
  //   `viewerMemberIdFromGraph` is null OR points at a different id, we
  //   call `bindViewerToMember(thatRow.id)`. That routes through the
  //   existing `claimProfile()` pipeline, which is idempotent on re-claim
  //   and unconditionally sets `viewerMemberId` in state (the same
  //   line-2695 path that "This is me / Center tree on me" already uses).
  //   This closes the runtime-synchronization gap the spec describes:
  //   "successful repair MUST set viewerMemberId, hydrate
  //   linked_person_id, hydrate tree_center_person_id, resolve
  //   bindingState='bound', center the tree on Marion Walker".
  //
  // PURE additive — never touches schema, RLS, auth providers, Stripe /
  // FamousPay / billing, invites pipeline, uploads, parent_ids, spouse_id,
  // kinship engine, tree layout, UI design, or any unrelated runtime
  // system. Only changes the repair handler's await safety + a post-
  // chain viewerMemberId re-sync that uses the already-locked
  // bindViewerToMember API.
  const handleRowRetry = useCallback(
    async (row: StatusRow) => {
      if (rowBusy[row.id]) return;
      setRowBusy((prev) => ({ ...prev, [row.id]: true }));

      // Per-step hard timeout — prevents any single stalled supabase
      // round-trip from holding the spinner open forever. Returns the
      // configured fallback value when the underlying promise hasn't
      // settled in `ms` milliseconds; the promise itself keeps running
      // in the background (no abort) and is allowed to resolve later —
      // we just don't keep the UI hostage on it.
      const withTimeout = async <T,>(
        p: Promise<T>,
        ms: number,
        fallback: T,
        tag: string,
      ): Promise<T> => {
        let to: ReturnType<typeof setTimeout> | null = null;
        const timer = new Promise<T>((resolve) => {
          to = setTimeout(() => {
            // eslint-disable-next-line no-console
            console.warn(`[SystemStatusPanel] step "${tag}" timed out after ${ms}ms — falling back`);
            resolve(fallback);
          }, ms);
        });
        try {
          const result = await Promise.race([p, timer]);
          return result;
        } finally {
          if (to) clearTimeout(to);
        }
      };

      try {
        const isIdentityRow = row.id === 'connection' || row.id === 'invites';
        if (isIdentityRow && authUserId) {
          // STEP 1 — Locked self-heal pipeline (idempotent / additive).
          // 6 s cap. On timeout we synthesize an `ok:false` result so
          // the chain continues to the refresh + viewer-resync steps.
          const heal = await withTimeout(
            attemptInvitedMemberSelfHeal({ authUserId, authEmail }),
            6000,
            {
              ok: false,
              correlation: 'selfheal-timeout',
              reason: 'self-heal timed out',
              steps: [],
            } as Awaited<ReturnType<typeof attemptInvitedMemberSelfHeal>>,
            'attemptInvitedMemberSelfHeal',
          );

          // STEP 2 — Auth profile refresh (also time-bounded).
          if (refreshProfile) {
            try {
              await withTimeout(refreshProfile(), 6000, undefined, 'refreshProfile');
            } catch {
              /* non-fatal */
            }
          }

          // STEP 3 — Family-graph refresh (also time-bounded). This is
          // the single most likely step to stall — refresh() chains
          // several supabase SELECTs and an optional auto-claim UPDATE.
          if (graphRefresh) {
            try {
              await withTimeout(graphRefresh(), 6000, undefined, 'graphRefresh');
            } catch {
              /* non-fatal */
            }
          }

          // STEP 4 — DEFENSIVE VIEWER RE-SYNC (the production fix).
          //
          // After the standard chain, look at the locally-loaded
          // `members` array. If we already have a row claimed by this
          // auth user (i.e. the DB is consistent — claim succeeded long
          // ago) but the in-memory `viewerMemberId` is null or points at
          // a different row, force the bind back into sync. This routes
          // through the existing `bindViewerToMember()` → `claimProfile()`
          // path, which:
          //   • is idempotent on re-claim (own-row re-claim is a no-op
          //     UPDATE that still triggers setViewerMemberId(memberId)),
          //   • respects the DB unique-claim partial index + the
          //     never-overwrite-another-user's-claim guard, and
          //   • unconditionally hydrates viewerMemberId in local state
          //     (FamilyGraphContext.tsx line 2695), which is exactly the
          //     value `linked_person_id` and `tree_center_person_id`
          //     read from in the diagnostic.
          //
          // The whole step is wrapped in withTimeout so it can never
          // hang the spinner either.
          try {
            if (authUserId && bindViewerToMember) {
              const myClaimedRow = members.find(
                (m) => m.claimedByUserId === authUserId,
              );
              if (
                myClaimedRow &&
                myClaimedRow.id !== viewerMemberIdFromGraph
              ) {
                await withTimeout(
                  bindViewerToMember(myClaimedRow.id),
                  6000,
                  undefined,
                  'bindViewerToMember-resync',
                );
              }
            }
          } catch (resyncErr) {
            // Non-fatal — the next refresh / next render will retry.
            // eslint-disable-next-line no-console
            console.warn(
              '[SystemStatusPanel] viewer-resync skipped (non-fatal)',
              resyncErr,
            );
          }

          // Trigger a re-run of the read-only diagnose so the row
          // re-derives its level on the very next render.
          setDiagBumpTick((t) => t + 1);

          if (heal.ok) {
            // If we still aren't bound after the self-heal, surface
            // the next manual step instead of silently claiming success.
            if (heal.inviterUserId && bindingState !== 'bound') {
              toast({
                title: 'Reconnecting…',
                description:
                  'We linked your invite. Open the family tree and tap "This is me" on yourself to finish identity binding.',
              });
              setRowSoftFailed((prev) => ({ ...prev, [row.id]: false }));
            } else {
              toast({
                title: 'Reconnected',
                description:
                  row.id === 'connection'
                    ? 'Your profile and family-tree binding are now in sync.'
                    : 'Your invites and account are now in sync.',
              });
              setRowSoftFailed((prev) => ({ ...prev, [row.id]: false }));
            }
          } else {
            // Self-heal failed OR timed out — surface a REAL error
            // (not silent success). Required by the spec: "failed
            // repair MUST show a real error instead of spinning
            // forever".
            toast({
              title: 'Still updating',
              description: heal.reason
                ? `Could not finish self-heal: ${heal.reason}. (correlation ${heal.correlation})`
                : `We couldn't finish self-heal automatically. (correlation ${heal.correlation})`,
            });
            setRowSoftFailed((prev) => ({ ...prev, [row.id]: true }));
          }
          return;
        }

        // Non-identity rows: keep the original generic refresh flow,
        // also time-bounded so a stalled `kc:family-graph-refresh`
        // dispatch can never hang this handler either.
        const ok = await withTimeout(
          runSystemRecovery({
            refreshProfile,
            source: `manual:${row.id}`,
          }),
          12000,
          false,
          `runSystemRecovery:${row.id}`,
        );
        setRowSoftFailed((prev) => ({ ...prev, [row.id]: !ok }));
        if (ok) {
          toast({
            title: 'Reconnecting…',
            description: 'KinsCircle re-checked this feature.',
          });
        }
      } finally {
        // ALWAYS clear the spinner. With per-step timeouts above, this
        // finally is guaranteed to run within ~18 s end-to-end, no
        // matter how stalled any underlying Supabase call gets.
        setRowBusy((prev) => ({ ...prev, [row.id]: false }));
      }
    },
    [
      rowBusy,
      refreshProfile,
      graphRefresh,
      bindViewerToMember,
      members,
      viewerMemberIdFromGraph,
      authUserId,
      authEmail,
      bindingState,
    ]
  );


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Activity className="w-5 h-5 text-fuchsia-600" />
            System Status
          </DialogTitle>
          <DialogDescription>
            A quick at-a-glance view of which parts of the app are working right now.
          </DialogDescription>
        </DialogHeader>

        {/* Manual Retry / Refresh System — re-runs the same safe refresh
            paths the app already uses (auth profile + family graph). It
            never deletes data and never creates duplicates. */}
        <div className="mt-1 mb-1 flex items-center justify-between gap-2 rounded-lg bg-slate-50 ring-1 ring-slate-200 px-3 py-2">
          <div className="text-xs text-slate-600 leading-tight">
            Something feels off? Re-check your profile, family graph, and memories without
            losing any data.
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleGlobalRetry}
            disabled={retrying}
            className="flex-shrink-0 h-8 px-2.5 text-xs gap-1.5"
            aria-label="Retry / Refresh System"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${retrying ? 'animate-spin' : ''}`} />
            {retrying ? 'Refreshing…' : 'Retry / Refresh System'}
          </Button>
        </div>

        <div className="mt-2 space-y-2.5">
          {STATUS_ROWS.map((row) => {
            const styles = LEVEL_STYLES[row.level];
            const { Badge } = styles;
            const isBusy = !!rowBusy[row.id];
            const showSoftFailed = !!rowSoftFailed[row.id];
            const showRetry =
              row.level !== 'ok' && !!row.retryLabel; // only non-OK rows get a CTA
            return (
              <div
                key={row.id}
                className={`flex items-start gap-3 rounded-xl ring-1 ${styles.ring} p-3.5`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <row.Icon className={`w-5 h-5 ${styles.text}`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-slate-900 text-sm">{row.label}</div>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${styles.pill} ${styles.pillText}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${styles.dot}`} />
                      {styles.label}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1 flex items-start gap-1.5">
                    <Badge className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${styles.text}`} />
                    <span>{row.detail}</span>
                  </div>

                  {showSoftFailed && row.reconnectMessage && (
                    <div className="mt-2 rounded-md bg-white/70 border border-slate-200 px-2 py-1.5 text-[11px] text-slate-700">
                      {row.reconnectMessage}
                    </div>
                  )}

                  {showRetry && (
                    <div className="mt-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleRowRetry(row)}
                        disabled={isBusy}
                        className="h-7 px-2 text-[11px] gap-1.5"
                      >
                        <RefreshCw
                          className={`w-3 h-3 ${isBusy ? 'animate-spin' : ''}`}
                        />
                        {isBusy ? 'Repairing…' : row.retryLabel}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Identity summary (read-only, tiny) ──────────────────────────
            Minimal four-field snapshot of the signed-in user's identity
            binding. Pure observation — never reads from Supabase, never
            mutates, never repairs. Just renders the values React already
            holds in context. */}
        {authUserId && (
          <div className="mt-3 rounded-xl ring-1 ring-slate-200 bg-slate-50/60 p-3">
            <div className="font-semibold text-slate-900 text-sm flex items-center gap-2">
              <Activity className="w-4 h-4 text-slate-600" />
              Identity summary
            </div>
            <div className="mt-2 grid grid-cols-1 gap-1 text-[11px] font-mono text-slate-700">
              <div className="flex items-start gap-2">
                <span className="text-slate-500 min-w-[150px]">members.length:</span>
                <span className="text-slate-900">{members.length}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-500 min-w-[150px]">viewerMemberId:</span>
                <span className="text-slate-900 break-all">
                  {viewerMemberIdFromGraph ?? '—'}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-500 min-w-[150px]">bindingState:</span>
                <span
                  className={`font-semibold ${
                    bindingState === 'bound'
                      ? 'text-emerald-700'
                      : 'text-amber-700'
                  }`}
                >
                  {bindingState}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-500 min-w-[150px]">is_center_correct:</span>
                <span
                  className={`font-semibold ${
                    bindingState === 'bound' &&
                    viewerMemberIdFromGraph &&
                    members.some(
                      (m) =>
                        m.id === viewerMemberIdFromGraph &&
                        m.claimedByUserId === authUserId,
                    )
                      ? 'text-emerald-700'
                      : 'text-amber-700'
                  }`}
                >
                  {bindingState === 'bound' &&
                  viewerMemberIdFromGraph &&
                  members.some(
                    (m) =>
                      m.id === viewerMemberIdFromGraph &&
                      m.claimedByUserId === authUserId,
                  )
                    ? 'true'
                    : 'false'}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-500 min-w-[150px]">lastRefreshError:</span>
                <span
                  className={`break-all ${
                    lastRefreshErrorFromGraph
                      ? 'text-rose-700 font-semibold'
                      : 'text-slate-900'
                  }`}
                >
                  {lastRefreshErrorFromGraph ?? 'none'}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-500 min-w-[150px]">targetOwner:</span>
                <span className="text-slate-900 break-all">
                  {lastTargetOwnerFromGraph ?? effectiveOwnerUserIdFromGraph ?? '—'}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-500 min-w-[150px]">authUserId:</span>
                <span className="text-slate-900 break-all">
                  {authUserId ?? '—'}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-500 min-w-[150px]">fetchedCount:</span>
                <span
                  className={`font-semibold ${
                    lastFetchedCountFromGraph > 0
                      ? 'text-emerald-700'
                      : 'text-amber-700'
                  }`}
                >
                  {lastFetchedCountFromGraph}
                </span>
              </div>
            </div>
          </div>
        )}






        {/* ── Spouse-link repair (DEV / STAFF ONLY — manual recovery) ─────
            Pure additive — only writes spouse_id (never parent_ids) so it
            cannot disturb tree placement, sibling/cousin/aunt logic, or
            any bloodline data. Used to fix cases where an earlier spouse
            add silently dropped the bidirectional spouse_id link.

            VISIBILITY GATE — both conditions required:
              1. running on a deploypad.app dev/staging host
                 (DEV_BYPASS_TEST_MODE === true)
              2. the authenticated user is staff (owner / admin role)

            On every production host (kinscircleapp.com / www.kinscircleapp.com
            / any non-deploypad domain) the entire repair block is omitted —
            no Heart icon, no "Repair spouse / partner link" header, no name
            dropdowns, no Auto-fix button, no result banner. Consumers see
            only the System Status rows + Retry / Refresh System button.

            The underlying `repairSpouseEdge` engine inside FamilyGraphContext
            is NOT removed — it remains fully functional and reachable from
            the dev GraphDebugPanel and any future internal repair pipeline.
            Only this UI surface is suppressed on production. */}
        {showDevRepairUI && repairSpouseEdge && members.length > 0 && (

          <div className="mt-3 rounded-xl ring-1 ring-pink-200 bg-pink-50/60 p-3.5">
            <div className="flex items-center gap-2 font-semibold text-sm text-pink-900">
              <Heart className="w-4 h-4 text-pink-600" />
              Repair spouse / partner link
            </div>
            <p className="text-[11px] text-pink-800/80 mt-1 leading-snug">
              If a spouse you added is showing as <strong>Needs connection</strong> instead
              of beside their partner, pick both relatives below and tap Repair. Only the
              spouse_id link is written — bloodline placement is never touched.
            </p>

            <div className="mt-2 grid grid-cols-1 gap-2">
              <select
                value={repairAId}
                onChange={(e) => {
                  setRepairAId(e.target.value);
                  setRepairResult(null);
                }}
                disabled={repairBusy}
                className="w-full rounded-md border border-pink-200 bg-white px-2 py-1.5 text-xs"
              >
                <option value="">— Pick first relative —</option>
                {members
                  .filter((m) => m.approvalStatus !== 'rejected')
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
              </select>
              <select
                value={repairBId}
                onChange={(e) => {
                  setRepairBId(e.target.value);
                  setRepairResult(null);
                }}
                disabled={repairBusy}
                className="w-full rounded-md border border-pink-200 bg-white px-2 py-1.5 text-xs"
              >
                <option value="">— Pick second relative (their spouse) —</option>
                {members
                  .filter((m) => m.approvalStatus !== 'rejected' && m.id !== repairAId)
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                onClick={handleRepairSpouse}
                disabled={repairBusy || !repairAId || !repairBId}
                className="bg-pink-600 hover:bg-pink-700 text-white h-8 text-xs gap-1.5"
              >
                <Heart className="w-3.5 h-3.5" />
                {repairBusy ? 'Repairing…' : 'Repair spouse link'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleAutoTerranceChekesha}
                disabled={repairBusy}
                className="h-8 text-xs gap-1.5 border-pink-300 text-pink-700 hover:bg-pink-50"
              >
                Auto-fix: Terrance Sr. + Chekesha
              </Button>
            </div>

            {repairResult && (
              <div className="mt-2 rounded-md bg-white/80 border border-pink-200 px-2 py-1.5 text-[11px] text-pink-900 leading-snug">
                {repairResult}
              </div>
            )}
          </div>
        )}

        {/* Legend */}
        <div className="mt-3 pt-3 border-t border-slate-100">

          <div className="flex items-center justify-center gap-4 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500" /> Working
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500" /> Updating
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-rose-500" /> Updating
            </span>
          </div>
          <div className="mt-2 text-center text-[10px] text-slate-400">
            Retry safely re-checks your profile, family graph, and memories — it never
            deletes data, duplicates profiles, or affects billing.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SystemStatusPanel;
